import { useState, useEffect } from 'react';
import { Bell, Trophy, Radio, Users, Calendar, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { API } from '@/App';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const NotificationsDropdown = ({ user, transparent = false }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      // Generate mock notifications based on live matches if API fails
      generateMockNotifications();
    } finally {
      setLoading(false);
    }
  };

  const generateMockNotifications = async () => {
    try {
      const response = await axios.get(`${API}/matches/live`);
      const liveMatches = response.data || [];
      
      const mockNotifications = liveMatches.slice(0, 3).map((match, idx) => ({
        id: `live-${match.id}`,
        type: 'live_match',
        title: 'Match Live Now!',
        message: `${match.team1_name || 'Team A'} vs ${match.team2_name || 'Team B'}`,
        match_id: match.id,
        created_at: new Date().toISOString(),
        read: false,
      }));

      // Add some general notifications
      mockNotifications.push({
        id: 'welcome',
        type: 'info',
        title: 'Welcome to CricORama!',
        message: 'Explore live matches and player stats',
        created_at: new Date(Date.now() - 3600000).toISOString(),
        read: true,
      });

      setNotifications(mockNotifications);
      setUnreadCount(mockNotifications.filter(n => !n.read).length);
    } catch (error) {
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  const markAsRead = async (notificationId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    
    if (notification.type === 'live_match' && notification.match_id) {
      navigate(`/live/${notification.match_id}`);
    } else if (notification.type === 'league') {
      navigate('/leagues');
    } else if (notification.type === 'stats') {
      navigate('/stats');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'live_match':
        return <Radio className="w-4 h-4 text-red-500" />;
      case 'league':
        return <Trophy className="w-4 h-4 text-amber-500" />;
      case 'team':
        return <Users className="w-4 h-4 text-blue-500" />;
      case 'schedule':
        return <Calendar className="w-4 h-4 text-green-500" />;
      default:
        return <Bell className="w-4 h-4 text-teal-500" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const clearAllNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon"
          className={`relative rounded-full ${
            transparent 
              ? 'hover:bg-white/10 text-white' 
              : 'hover:bg-gray-100 text-gray-600'
          }`}
          data-testid="notifications-btn"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <h3 className="font-semibold text-sm">Notifications</h3>
          {notifications.length > 0 && (
            <button 
              onClick={clearAllNotifications}
              className="text-xs text-gray-500 hover:text-red-500 transition-colors"
            >
              Clear all
            </button>
          )}
        </div>
        
        {loading ? (
          <div className="px-3 py-8 text-center">
            <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="px-3 py-8 text-center text-gray-500">
            <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No notifications yet</p>
          </div>
        ) : (
          <>
            {notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`flex items-start gap-3 px-3 py-3 cursor-pointer ${
                  !notification.read ? 'bg-teal-50/50' : ''
                }`}
              >
                <div className="mt-0.5">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${!notification.read ? 'font-semibold' : 'font-medium'}`}>
                    {notification.title}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {notification.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {formatTime(notification.created_at)}
                  </p>
                </div>
                {!notification.read && (
                  <div className="w-2 h-2 bg-teal-500 rounded-full mt-2"></div>
                )}
              </DropdownMenuItem>
            ))}
          </>
        )}
        
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          onClick={() => navigate('/live')}
          className="text-center text-sm text-teal-600 hover:text-teal-700 cursor-pointer justify-center"
        >
          View all live matches
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationsDropdown;
